import { jwtDecode, type JwtPayload } from 'jwt-decode'
import Keycloak from 'keycloak-js'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useTimer } from '@/composables'
import { isLocal } from '@/environments'
import { useAppStore } from '@/stores/app.ts'

export const useTokenStore = defineStore(
  'token',
  () => {
    const access = ref<string | undefined>(undefined)
    const accessDecoded = ref<JwtPayload | undefined>(undefined)
    const refresh = ref<string | undefined>(undefined)
    const timeSkew = ref<number>(0)
    const redirect = ref<string>()
    const router = useRouter()

    const { start: startTimer, stop: stopTimer, isRunning: isTimerRunning } = useTimer()

    const client = ref<Keycloak>()

    function getClient() {
      if (!client.value) {
        const app = useAppStore()

        client.value = new Keycloak({
          url: `${app.setting('auth.url')}`,
          realm: `${app.setting('auth.realm')}`,
          clientId: `${app.setting('auth.client.id')}`,
        })
      }

      return client.value
    }

    function setAccess(token: string | undefined) {
      access.value = token
      if (!access.value) return

      try {
        accessDecoded.value = jwtDecode(access.value)
      } catch (error) {
        // A corrupted, truncated, or non-JWT value (e.g. a stale value in localStorage) must
        // not crash hydration, which runs before the app can render.
        //
        // Locally the API's Keycloak guard also accepts a bare user id as a bearer token
        // (see vendor/atabix/laravel-keycloak KeycloakGuard, the `App::isLocal()` branch),
        // which is legitimately not a JWT. There, only the decoded claims are unavailable --
        // the token itself still authenticates -- so it is kept.
        accessDecoded.value = undefined

        if (isLocal) {
          console.warn('[ATABASE] Access token is not a JWT; keeping it without claims:', error)

          return
        }

        // Deployed environments only ever issue real JWTs, so one that cannot be decoded is
        // unusable. Treat it as absent and let the app re-authenticate normally.
        access.value = undefined
      }
    }

    function setTimeSkew() {
      if (accessDecoded.value?.iat) {
        timeSkew.value = Math.floor(Date.now() / 1000) - accessDecoded.value.iat
      }

      if (isLocal) console.log(`[ATABASE] Estimated time difference between browser and server is ${Math.round(timeSkew.value)} seconds`)
    }

    function setRefresh(token: string | undefined) {
      refresh.value = token
    }

    async function setInterval(callback: () => Promise<void>, ms: number): Promise<void> {
      if (!access.value) return

      // Initiate a refresh immediately in case the token has already expired.
      await callback()

      // Start the timer for any additional checks.
      startTimer(callback, ms)
    }

    function clearInterval() {
      stopTimer()
    }

    function clear() {
      clearInterval()
      setAccess(undefined)
      setRefresh(undefined)
    }

    function isExpired(minValidity): boolean {
      if (!access.value) return true
      if (!accessDecoded.value) return true

      const expirationDate = accessDecoded.value.exp
      const timeSkewDate = accessDecoded.value.iat
      if (!expirationDate || !timeSkewDate) return true

      let expiresIn = expirationDate - Math.ceil(Date.now() / 1000) + timeSkew.value // Seconds
      if (minValidity) expiresIn -= minValidity

      if (isLocal) console.log(`[ATABASE] Token expires in ${Math.round(expiresIn)} seconds.`)

      return expiresIn <= 0
    }

    function setRedirect(url: string) {
      redirect.value = url
    }

    function useRedirect() {
      if (!redirect.value) return

      const path = redirect.value
      redirect.value = undefined
      // Defer so we don't fight the in-flight navigation guard. The factory in
      // @atabix/vue-ui ignores guard return values and always calls next() at
      // the end, so any router.push inside a guard would be swallowed.
      setTimeout(() => router.push(path), 0)
    }

    return {
      access,
      refresh,
      getClient,
      setAccess,
      setRefresh,
      setTimeSkew,
      isExpired,
      isTimerRunning,
      clear,
      setInterval,
      clearInterval,
      redirect,
      setRedirect,
      useRedirect,
    }
  },
  {
    persist: {
      pick: ['access', 'refresh', 'timeSkew', 'redirect'],
      afterHydrate(context) {
        context.store.setAccess(context.store.access)
        context.store.setRefresh(context.store.refresh)
      },
      storage: localStorage,
    },
  },
)
